import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Globe,
  Plus,
  RefreshCw,
  AlertTriangle,
  Search,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import QaDiagnosticsPanel from './QaDiagnosticsPanel';

const CATEGORY_OPTIONS = [
  { value: 'bank', label: 'Bank' },
  { value: 'credit_union', label: 'Credit Union' },
  { value: 'lender', label: 'Lender' },
  { value: 'business_funding', label: 'Business Funding' },
  { value: 'government', label: 'Government' },
  { value: 'business_service', label: 'Business Service' },
  { value: 'other', label: 'Other' },
  { value: 'pending_review', label: 'Pending review' },
];

function categoryLabel(value) {
  return CATEGORY_OPTIONS.find((item) => item.value === value)?.label || value || '—';
}

function sourceLabel(value) {
  if (value === 'admin_auto_approve') return 'Admin auto-approve';
  return 'Manual';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function safeErrorMessage(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Unable to update QA Website Access.';
  const lowered = message.toLowerCase();
  if (lowered.includes('traceback') || lowered.includes('password') || lowered.includes('secret')) {
    return 'Unable to update QA Website Access.';
  }
  return message;
}

const emptySummary = {
  approvalMode: 'manual',
  activeWebsites: 0,
  disabledWebsites: 0,
  pendingReviewWebsites: 0,
  qaUsageToday: 0,
};

const QaWebsiteAccessPage = () => {
  const { getAuthHeaders } = useAuth();
  const [summary, setSummary] = useState(emptySummary);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [modeConfirm, setModeConfirm] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addCategory, setAddCategory] = useState('pending_review');
  const [addNotes, setAddNotes] = useState('');
  const [addSubdomains, setAddSubdomains] = useState('');
  const [includeRegistrable, setIncludeRegistrable] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [actionConfirm, setActionConfirm] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [editCategory, setEditCategory] = useState('');
  const [diagRuns, setDiagRuns] = useState([]);
  const [diagAssignments, setDiagAssignments] = useState([]);

  const auth = () => adminOpsService.setAuthHeaders(getAuthHeaders());

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      auth();
      const [summaryPayload, listPayload, modePayload, runsPayload, assignmentsPayload] = await Promise.all([
        adminOpsService.getQaWebsiteAccessSummary(),
        adminOpsService.getQaWebsiteAccess({
          q: search,
          category: categoryFilter,
          status: statusFilter,
          approval_source: sourceFilter,
        }),
        adminOpsService.getQaWebsiteAccessApprovalMode(),
        adminOpsService.listQaDiagnosticsRuns(),
        adminOpsService.getQaDiagnosticsAssignments(),
      ]);
      setSummary({
        ...emptySummary,
        ...(summaryPayload || {}),
        approvalMode: modePayload?.approvalMode || summaryPayload?.approvalMode || 'manual',
      });
      setItems(listPayload?.items || []);
      setDiagRuns(runsPayload?.items || []);
      setDiagAssignments(assignmentsPayload?.items || []);
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approvalMode = summary.approvalMode === 'admin_auto_approve' ? 'admin_auto_approve' : 'manual';

  const filteredHint = useMemo(() => {
    if (!search && !categoryFilter && !statusFilter && !sourceFilter) return '';
    return `${items.length} matching websites`;
  }, [items.length, search, categoryFilter, statusFilter, sourceFilter]);

  const applyFilters = async (event) => {
    event?.preventDefault?.();
    await load();
  };

  const confirmModeChange = async () => {
    if (!modeConfirm) return;
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.putQaWebsiteAccessApprovalMode(modeConfirm);
      setSummary((current) => ({ ...current, approvalMode: payload.approvalMode }));
      setModeConfirm(null);
      toast.success(payload.approvalMode === 'admin_auto_approve' ? 'Admin auto-approve enabled' : 'Manual approval enabled');
    } catch (err) {
      toast.error(safeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAnalyze = async (event) => {
    event?.preventDefault?.();
    setBusy(true);
    setApproveConfirm(false);
    try {
      auth();
      const payload = await adminOpsService.analyzeQaWebsiteAccess(addUrl);
      setAnalysis(payload);
      if (payload?.suggestedCategory) setAddCategory(payload.suggestedCategory);
      if (payload?.includeRegistrableDefault === false) setIncludeRegistrable(false);
    } catch (err) {
      toast.error(safeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    setBusy(true);
    try {
      auth();
      await adminOpsService.createQaWebsiteAccess({
        url: addUrl,
        category: addCategory,
        notes: addNotes,
        explicitSubdomains: addSubdomains
          ? addSubdomains.split(/[\s,]+/).map((item) => item.trim()).filter(Boolean)
          : [],
        includeRegistrableDomain: includeRegistrable,
      });
      toast.success('Website added to the shared allowlist');
      setAddOpen(false);
      setAddUrl('');
      setAddNotes('');
      setAddSubdomains('');
      setAnalysis(null);
      setApproveConfirm(false);
      await load();
    } catch (err) {
      toast.error(safeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openDetails = async (item) => {
    setDetailsItem(item);
    setEditCategory(item.category || '');
    setAuditItems([]);
    try {
      auth();
      const payload = await adminOpsService.getQaWebsiteAccessAudit(item.id);
      setAuditItems(payload?.items || []);
    } catch (err) {
      toast.error(safeErrorMessage(err));
    }
  };

  const saveCategory = async () => {
    if (!detailsItem) return;
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.patchQaWebsiteAccess(detailsItem.id, { category: editCategory });
      setDetailsItem(payload);
      toast.success('Category updated');
      await load();
    } catch (err) {
      toast.error(safeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runConfirmedAction = async () => {
    if (!actionConfirm) return;
    setBusy(true);
    try {
      auth();
      if (actionConfirm.type === 'disable') {
        await adminOpsService.disableQaWebsiteAccess(actionConfirm.item.id);
        toast.success('Website disabled');
      } else if (actionConfirm.type === 'enable') {
        await adminOpsService.enableQaWebsiteAccess(actionConfirm.item.id);
        toast.success('Website re-enabled');
      } else if (actionConfirm.type === 'delete') {
        await adminOpsService.deleteQaWebsiteAccess(actionConfirm.item.id);
        toast.success('Website removed');
        setDetailsItem(null);
      }
      setActionConfirm(null);
      await load();
    } catch (err) {
      toast.error(safeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="qa-website-access-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Globe className="text-amber-400" size={28} />
            QA Website Access
          </h1>
          <p className="text-slate-500 mt-1">
            Manage the shared website allowlist used by Authorized QA Proxy sessions.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={load} disabled={loading || busy}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => { setAddOpen(true); setAnalysis(null); setApproveConfirm(false); }}
            data-testid="qa-website-access-add"
          >
            <Plus size={16} className="mr-2" />
            Add approved website
          </Button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="qa-website-access-error">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, index) => <Skeleton key={index} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Approval mode</p>
            <p className="text-lg font-semibold text-white" data-testid="qa-website-access-mode-value">
              {approvalMode === 'admin_auto_approve' ? 'Admin auto-approve' : 'Manual approval'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Active websites</p>
            <p className="text-3xl font-bold text-emerald-400" data-testid="qa-website-access-active-count">{summary.activeWebsites || 0}</p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Disabled websites</p>
            <p className="text-3xl font-bold text-amber-400" data-testid="qa-website-access-disabled-count">{summary.disabledWebsites || 0}</p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Pending-review websites</p>
            <p className="text-3xl font-bold text-sky-400" data-testid="qa-website-access-pending-count">{summary.pendingReviewWebsites || 0}</p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">QA usage today</p>
            <p className="text-3xl font-bold text-white" data-testid="qa-website-access-usage-count">{summary.qaUsageToday || 0}</p>
          </div>
        </div>
      )}

      <QaDiagnosticsPanel
        auth={auth}
        busy={busy}
        setBusy={setBusy}
        runs={diagRuns}
        assignments={diagAssignments}
        onRefresh={load}
      />

      <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-3">
        <p className="text-sm font-medium text-white">Approval mode</p>
        <div className="inline-flex rounded-lg border border-stone-700 overflow-hidden" data-testid="qa-website-access-mode-control">
          <button
            type="button"
            className={`px-4 py-2 text-sm ${approvalMode === 'manual' ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}
            data-testid="qa-website-access-mode-manual"
            onClick={() => approvalMode !== 'manual' && setModeConfirm('manual')}
          >
            Manual approval
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm ${approvalMode === 'admin_auto_approve' ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}
            data-testid="qa-website-access-mode-auto"
            onClick={() => approvalMode !== 'admin_auto_approve' && setModeConfirm('admin_auto_approve')}
          >
            Admin auto-approve
          </button>
        </div>
        <p className="text-sm text-slate-400">
          Admin auto-approve adds eligible websites opened by a platform admin to the shared QA Proxy allowlist. Restricted categories remain blocked.
        </p>
      </div>

      <form className="bg-stone-900 border border-stone-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3" onSubmit={applyFilters}>
        <label className="md:col-span-2 flex items-center gap-2 bg-stone-950 border border-stone-800 rounded-lg px-3">
          <Search size={16} className="text-slate-500" />
          <input
            className="w-full bg-transparent py-2 text-sm text-white outline-none"
            placeholder="Search domain"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="qa-website-access-search"
          />
        </label>
        <select className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} data-testid="qa-website-access-filter-category">
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
        <select className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} data-testid="qa-website-access-filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
          <option value="pending_review">Pending review</option>
        </select>
        <div className="flex gap-2">
          <select className="flex-1 bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} data-testid="qa-website-access-filter-source">
            <option value="">All sources</option>
            <option value="manual">Manual</option>
            <option value="admin_auto_approve">Admin auto-approve</option>
          </select>
          <Button type="submit" variant="outline" data-testid="qa-website-access-apply-filters">Filter</Button>
        </div>
        {filteredHint ? <p className="md:col-span-5 text-xs text-slate-500">{filteredHint}</p> : null}
      </form>

      <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="qa-website-access-table">
          <thead className="text-left text-slate-500 border-b border-stone-800">
            <tr>
              <th className="p-3">Domain</th>
              <th className="p-3">Category</th>
              <th className="p-3">Status</th>
              <th className="p-3">Approval source</th>
              <th className="p-3">Approved by</th>
              <th className="p-3">Approved date</th>
              <th className="p-3">Last used</th>
              <th className="p-3">Usage count</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-stone-800/70 text-slate-200">
                <td className="p-3">{item.normalizedHost}</td>
                <td className="p-3">{categoryLabel(item.category)}</td>
                <td className="p-3">{item.status === 'disabled' ? 'Disabled' : item.category === 'pending_review' ? 'Pending review' : 'Active'}</td>
                <td className="p-3">{sourceLabel(item.approvalSource)}</td>
                <td className="p-3">{item.approvedByDisplayName || '—'}</td>
                <td className="p-3">{formatDate(item.approvedAt)}</td>
                <td className="p-3">{formatDate(item.lastUsedAt)}</td>
                <td className="p-3">{item.usageCount || 0}</td>
                <td className="p-3 space-x-2 whitespace-nowrap">
                  <button type="button" className="text-sky-400" onClick={() => openDetails(item)} data-testid={`qa-website-access-view-${item.normalizedHost}`}>View</button>
                  {item.status === 'disabled' ? (
                    <button type="button" className="text-emerald-400" onClick={() => setActionConfirm({ type: 'enable', item })} data-testid={`qa-website-access-enable-${item.normalizedHost}`}>Re-enable</button>
                  ) : (
                    <button type="button" className="text-amber-400" onClick={() => setActionConfirm({ type: 'disable', item })} data-testid={`qa-website-access-disable-${item.normalizedHost}`}>Disable</button>
                  )}
                  <button type="button" className="text-red-400" onClick={() => setActionConfirm({ type: 'delete', item })} data-testid={`qa-website-access-delete-${item.normalizedHost}`}>Delete</button>
                </td>
              </tr>
            ))}
            {!items.length && !loading ? (
              <tr><td className="p-6 text-slate-500" colSpan={9}>No websites in the shared catalog yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {modeConfirm ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3" data-testid="qa-website-access-mode-confirm">
          <p className="text-sm text-amber-100">
            {modeConfirm === 'admin_auto_approve'
              ? 'Switch to Admin auto-approve? Eligible websites opened by a platform admin QA session will be added to the shared allowlist. Restricted categories remain blocked.'
              : 'Switch to Manual approval? New domains stay blocked until a platform admin adds them here.'}
          </p>
          <div className="flex gap-2">
            <Button onClick={confirmModeChange} disabled={busy} data-testid="qa-website-access-mode-confirm-yes">Confirm</Button>
            <Button variant="outline" onClick={() => setModeConfirm(null)}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {addOpen ? (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4" data-testid="qa-website-access-add-form">
          <h2 className="text-lg font-semibold text-white">Add approved website</h2>
          <form className="space-y-3" onSubmit={handleAnalyze}>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              placeholder="https://www.bigthinkcapital.com/"
              value={addUrl}
              onChange={(event) => setAddUrl(event.target.value)}
              data-testid="qa-website-access-url-input"
            />
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              placeholder="Optional explicit subdomains"
              value={addSubdomains}
              onChange={(event) => setAddSubdomains(event.target.value)}
              data-testid="qa-website-access-subdomains-input"
            />
            <textarea
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              placeholder="Notes for internal admin use"
              value={addNotes}
              onChange={(event) => setAddNotes(event.target.value)}
              data-testid="qa-website-access-notes-input"
            />
            <select className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={addCategory} onChange={(event) => setAddCategory(event.target.value)} data-testid="qa-website-access-category-select">
              {CATEGORY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={includeRegistrable} onChange={(event) => setIncludeRegistrable(event.target.checked)} data-testid="qa-website-access-include-registrable" />
              Also approve the registrable domain when it differs from this host
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} data-testid="qa-website-access-analyze">Analyze</Button>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </form>
          {analysis ? (
            <div className="bg-stone-950 border border-stone-800 rounded-lg p-4 space-y-2" data-testid="qa-website-access-analysis">
              <p className="text-sm text-slate-300">Suggested category: {analysis.suggestedCategoryLabel || categoryLabel(analysis.suggestedCategory)} (suggestion only)</p>
              <p className="text-xs text-slate-500">{analysis.suggestedCategoryReason}</p>
              {analysis.eligible === false ? <p className="text-sm text-red-300">{analysis.reason}</p> : null}
              {analysis.eligible ? (
                approveConfirm ? (
                  <div data-testid="qa-website-access-approve-confirm">
                    <p className="text-sm text-amber-100">{analysis.confirmationText}</p>
                    <Button className="mt-2" onClick={handleCreate} disabled={busy} data-testid="qa-website-access-approve-confirm-yes">Approve</Button>
                  </div>
                ) : (
                  <Button onClick={() => setApproveConfirm(true)} data-testid="qa-website-access-approve">Continue to approve</Button>
                )
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {actionConfirm ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3" data-testid="qa-website-access-action-confirm">
          <p className="text-sm text-amber-100">
            {actionConfirm.type === 'delete'
              ? `Remove ${actionConfirm.item.normalizedHost} from the shared QA Proxy allowlist? Existing QA sessions will stop reaching it after cache invalidation.`
              : actionConfirm.type === 'disable'
                ? `Disable ${actionConfirm.item.normalizedHost}? New and existing QA sessions will be blocked immediately after cache invalidation.`
                : `Re-enable ${actionConfirm.item.normalizedHost} on the shared QA Proxy allowlist?`}
          </p>
          <div className="flex gap-2">
            <Button onClick={runConfirmedAction} disabled={busy} data-testid="qa-website-access-action-confirm-yes">Confirm</Button>
            <Button variant="outline" onClick={() => setActionConfirm(null)}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {detailsItem ? (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4" data-testid="qa-website-access-details">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">{detailsItem.normalizedHost}</h2>
            <Button variant="outline" onClick={() => setDetailsItem(null)}>Close</Button>
          </div>
          <p className="text-sm text-slate-400">Registrable domain: {detailsItem.registrableDomain}</p>
          <div className="flex gap-2 items-center">
            <select className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={editCategory} onChange={(event) => setEditCategory(event.target.value)} data-testid="qa-website-access-edit-category">
              {CATEGORY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <Button onClick={saveCategory} disabled={busy} data-testid="qa-website-access-save-category">Save category</Button>
          </div>
          <div>
            <h3 className="text-sm font-medium text-white mb-2">Audit history</h3>
            <ul className="space-y-1 text-sm text-slate-400" data-testid="qa-website-access-audit">
              {auditItems.map((item) => (
                <li key={item.id}>{item.action} — {item.reason || item.outcome || '—'} — {formatDate(item.createdAt)}</li>
              ))}
              {!auditItems.length ? <li>No audit events yet.</li> : null}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QaWebsiteAccessPage;
